/**
 * Sub-rogar um título ou enviá-lo para o jurídico (AF-3674).
 *
 * Os dois atos são gêmeos — muda o rótulo e a rota —, e o que o modal existe para resolver é a
 * PERGUNTA que vem junto: o que fazer com as faturas que continuavam sendo cobradas. Confirmar sem
 * responder isso deixaria a cobrança correndo sobre uma dívida que já não é nossa.
 *
 * ⚠️ O efeito é aplicado NA HORA, diferente da marcação de pagamento (cujo `payment_action_promise`
 * só acontece na aprovação). Por isso o texto fala no presente.
 *
 * ⚠️ Só a SUB-ROGAÇÃO pergunta, e nasce sem escolha: cancelar é a mais destrutiva das duas, e um
 * default que ninguém leu vira decisão que ninguém tomou.
 *
 * ⚠️ O envio para o jurídico NÃO pergunta — ele sempre para de cobrar. As faturas continuam
 * existindo, porque é delas que a ação judicial trata; cancelá-las destruiria o objeto do
 * processo. Quem decide isso é o servidor, não esta tela: mandar a escolha daqui deixaria outro
 * cliente livre para pedir o contrário.
 */
import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import { FaHandHoldingDollar, FaScaleBalanced } from 'react-icons/fa6';

import { flagTitleDebt, TitleDebtFlag, OpenInvoicesAction } from '../services/titleDebtActions';

/** Tudo o que muda entre os dois atos. Um só componente porque o resto é idêntico. */
const ACTS: Record<TitleDebtFlag, {
    title: string; icon: JSX.Element; verb: string; confirm: string;
    /** o que acontece com as faturas abertas — ausente quando o ato pergunta */
    fixedAction?: { label: string; hint: string };
}> = {
    SUBROGATION: {
        title: 'Sub-rogar Título',
        icon: <FaHandHoldingDollar className="text-slate-500" />,
        verb: 'sub-rogado',
        confirm: 'Sub-rogar',
    },
    JUDICIAL_PETITION: {
        title: 'Enviar para Jurídico',
        icon: <FaScaleBalanced className="text-slate-500" />,
        verb: 'enviado para o jurídico',
        confirm: 'Enviar para Jurídico',
        fixedAction: {
            label: 'As faturas abertas deixam de ser cobradas.',
            hint: 'Elas continuam existindo — é delas que a ação judicial trata. Não são '
                + 'canceladas.',
        },
    },
};

const CHOICES: { value: OpenInvoicesAction; label: string; hint: string }[] = [
    {
        value: 'CANCEL',
        label: 'Cancelar faturas abertas',
        hint: 'As faturas deixam de existir como cobrança. Com boleto emitido, o cancelamento é '
            + 'enviado ao banco.',
    },
    {
        value: 'UNCOLLECT',
        label: 'Parar de cobrar faturas abertas',
        hint: 'As faturas continuam existindo, mas saem da régua de cobrança.',
    },
];

interface Props {
    charge: any;
    flag: TitleDebtFlag;
    onClose: () => void;
    onSuccess: () => void;
}

const FlagTitleDebtModal: React.FC<Props> = ({ charge, flag, onClose, onSuccess }) => {
    const [choice, setChoice] = useState<OpenInvoicesAction | ''>('');
    const [submitting, setSubmitting] = useState(false);
    const act = ACTS[flag];
    // ato com efeito fixo não pergunta, então não há o que esperar do usuário
    const ready = !!act.fixedAction || !!choice;

    const handleConfirm = async () => {
        if (!ready) return;
        setSubmitting(true);
        try {
            // sem escolha no ato fixo: quem a define é o servidor
            await flagTitleDebt(charge.titleDebtId, flag,
                act.fixedAction ? undefined : (choice as OpenInvoicesAction));
            toast.success(`Título ${act.verb}.`);
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(e.message || `Erro ao registrar: título não foi ${act.verb}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        {act.icon} {act.title}
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                </div>

                <p className="text-xs text-gray-500 mb-3">
                    Contrato {charge.contractLabel || charge.contractNumber || '—'}
                    {charge.documentTitle ? ` · Título/NF ${charge.documentTitle}` : ''}
                    {' · '}a cobrança deste título deixa de ser nossa.
                    {' '}<strong>A ação não pode ser desfeita pela tela.</strong>
                </p>

                {act.fixedAction ? (
                    /* sem pergunta: o efeito é parte do ato. Dito mesmo assim, porque o usuário
                       precisa saber o que vai acontecer com o que estava sendo cobrado */
                    <div className="mb-3 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 rounded">
                        {act.fixedAction.label}
                        <span className="block text-xs text-gray-500 mt-0.5">
                            {act.fixedAction.hint}
                        </span>
                    </div>
                ) : (
                    <fieldset className="mb-3">
                        <legend className="block text-sm font-medium mb-2">
                            O que fazer com as faturas abertas deste título?
                        </legend>
                        {CHOICES.map(option => (
                            <label key={option.value}
                                className="flex items-start gap-2 text-sm mb-2 cursor-pointer">
                                <input type="radio" name="open-invoices-action" className="mt-1"
                                    value={option.value} checked={choice === option.value}
                                    onChange={() => setChoice(option.value)} />
                                <span>
                                    {option.label}
                                    <span className="block text-xs text-gray-500">{option.hint}</span>
                                </span>
                            </label>
                        ))}
                    </fieldset>
                )}

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={handleConfirm} disabled={!ready || submitting}
                        title={ready ? '' : 'Escolha o que fazer com as faturas abertas'}
                        className="px-4 py-2 text-sm rounded bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed">
                        {submitting ? 'Registrando...' : act.confirm}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FlagTitleDebtModal;
